import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { DamageTypeWithLineItems } from '../../../../common/damage';
import type { SubAsset } from '../../../../common/sub-asset';
import { buildSubAssetPath, getTreeView } from '../../../../lib/utils/asset';
import { getListSubAssetsOptions } from '../../../../queries/sub-assets/list-sub-assets';
import { Badge } from '../../../ui/badge';
import { DamageLineItems } from './DamageLineItems';

type SubAssetWithDamages = SubAsset & {
	damages?: DamageTypeWithLineItems[];
};

export type DamagesListViewProps = {
	linkedDamages?: DamageTypeWithLineItems[];
};
export function DamagesListView({ linkedDamages }: DamagesListViewProps) {
	const { t } = useTranslation();
	const { assetId } = useParams<{ assetId: string }>();
	// const { data: listDamageResponse } = useQuery(getListDamagesOptions({ assetId, offset: 0, limit: 300 }, !!assetId));
	const damages = linkedDamages || [];
	console.log('DamagesListView - damages:', damages);
	const asset = damages[0]?.asset;
	const inspections = useMemo(() => {
		const inspectionSet = new Set<string>();
		damages.forEach((damage) => {
			if (damage.inspection) {
				inspectionSet.add(damage.inspection.title);
			}
		});
		return Array.from(inspectionSet);
	}, [damages]);

	const { data: subAssetsResponse } = useQuery(getListSubAssetsOptions(assetId || '', { limit: 300, offset: 0 }));
	const subAssets = subAssetsResponse?.subAssets || [];

	const subAssetsWithFirstParent = useMemo((): SubAsset[] => {
		// If no sub-assets, return empty array
		if (!subAssets?.length || !asset?.id) return [];

		// Use getTreeView to structure sub-assets hierarchically
		const children = getTreeView(subAssets);
		return [
			{
				id: asset?.id as string,
				name: asset?.name || '',
				parentSubAssetId: null,
				children,
			},
		];
	}, [subAssets, asset?.id, asset?.name]);

	const getAssetBreadcrumbPath = useCallback(
		(assetId: string): string => {
			const path = buildSubAssetPath(subAssetsWithFirstParent, assetId);

			return path.map((s) => s.name || '').join(' > ');
		},
		[subAssetsWithFirstParent],
	);

	const subAssetsWithDamage = useMemo(() => {
		const grouped: { [key: string]: typeof damages } = {};
		damages.forEach((damage) => {
			const subAssetId = damage.subAssetId || damage.assetId || 'unknown';
			if (!grouped[subAssetId]) {
				grouped[subAssetId] = [];
			}
			grouped[subAssetId].push(damage);
		});
		const recursiveChildrenWithDamages = (subAssets: SubAsset[]): SubAssetWithDamages[] => {
			return subAssets.map((subAsset) => {
				const childrenWithDamages = subAsset.children ? recursiveChildrenWithDamages(subAsset.children) : undefined;
				return {
					...subAsset,
					name: getAssetBreadcrumbPath(subAsset.id as string),
					children: childrenWithDamages,
					damages: grouped[subAsset.id as string] || [],
				};
			});
		};
		return recursiveChildrenWithDamages(subAssetsWithFirstParent);
	}, [damages, subAssetsWithFirstParent, getAssetBreadcrumbPath]);

	return (
		<div className="flex-1 overflow-auto">
			<div className="space-y-4">
				<div className="flex items-center justify-between">
					<h2 className="text-xl font-semibold">{t('finance.damages.listTitle')}</h2>
					<div className="flex gap-2">
						<Button variant="secondary" className="cursor-pointer">
							{t('finance.damages.exportPdf')}
						</Button>
						<Button variant="secondary" className="cursor-pointer">
							{t('finance.damages.gaebExport')}
						</Button>
					</div>
				</div>
				<div className="rounded-lg border text-card-foreground bg-card shadow-sm">
					<div className="p-6 pt-6">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<span className="text-sm font-medium text-muted-foreground">{t('finance.damages.asset')}</span>
								<div>
									<Badge className="mr-2 mb-2 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
										{asset?.name}
									</Badge>
								</div>
							</div>
							<div>
								<span className="text-sm font-medium text-muted-foreground">{t('finance.damages.inspections')}</span>
								<div>
									{inspections.map((inspection) => (
										<Badge
											key={inspection}
											className="mr-2 mb-2 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium"
										>
											{inspection}
										</Badge>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="bg-card rounded-lg border overflow-hidden shadow-sm">
					<div className="relative w-full overflow-auto">
						<Table>
							<TableHeader>
								<TableRow className="border-b transition-colors bg-background hover:bg-background">
									<TableHead className="font-semibold w-30">{t('finance.damages.table.damageId')}</TableHead>
									<TableHead className="font-semibold">{t('finance.damages.table.damageTitle')}</TableHead>
									<TableHead className="font-semibold w-35">{t('common.labels.status')}</TableHead>
									<TableHead className="font-semibold w-25">{t('finance.damages.class')}</TableHead>
									<TableHead className="font-semibold w-35">{t('finance.damages.material')}</TableHead>
									<TableHead className="font-semibold w-35">{t('finance.damages.table.damageType')}</TableHead>
									<TableHead className="font-semibold w-25">{t('finance.damages.table.lengthArea')}</TableHead>
									<TableHead className="font-semibold w-20">{t('finance.items.unit')}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								<DamageLineItems subAssetWithDamages={subAssetsWithDamage[0]} />
								{!damages?.length && (
									<TableRow>
										<TableCell className="text-center text-muted-foreground py-8" colSpan={8}>
											{t('finance.damages.empty')}
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
				</div>
			</div>
		</div>
	);
}
